import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/cn';

export const DropdownMenu = Dropdown.Root;
export const DropdownMenuTrigger = Dropdown.Trigger;

interface ContentProps extends ComponentPropsWithoutRef<typeof Dropdown.Content> {
  children: ReactNode;
}

export const DropdownMenuContent = forwardRef<HTMLDivElement, ContentProps>(function DropdownMenuContent(
  { className, sideOffset = 4, ...rest },
  ref,
) {
  return (
    <Dropdown.Portal>
      <Dropdown.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[10rem] overflow-hidden rounded-md border border-parchment-border bg-parchment-light p-1 shadow-parchment-card',
          'animate-fade-in',
          className,
        )}
        {...rest}
      />
    </Dropdown.Portal>
  );
});

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Dropdown.Item> & { destructive?: boolean }
>(function DropdownMenuItem({ className, destructive, ...rest }, ref) {
  return (
    <Dropdown.Item
      ref={ref}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
        'hover:bg-parchment-dark/50 focus:bg-parchment-dark/50',
        'outline-none',
        destructive && 'text-danger hover:bg-danger/10 focus:bg-danger/10',
        className,
      )}
      {...rest}
    />
  );
});

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Dropdown.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return (
    <Dropdown.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-parchment-border', className)}
      {...rest}
    />
  );
});
